import type { StaffProfileResponse, StaffRosterResponse, StaffTicketCountsResponse } from '../types/staff';

const STAFF_API_BASE_URL = 'https://spy.playcdu.co/api/notes/staff';

// Roster is shared by the list view and the profile-page rail; profiles are
// re-read on every navigation unless recently fetched. Both caches are
// per-session and can be bypassed with `force`.
const ROSTER_TTL_MS = 5 * 60 * 1000;
const PROFILE_TTL_MS = 2 * 60 * 1000;
// Ticket counts are live for the current month; the backend already holds them for five
// minutes, so re-reading sooner than that only returns the same snapshot.
const TICKET_COUNTS_TTL_MS = 5 * 60 * 1000;

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export class StaffApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'StaffApiError';
    this.status = status;
  }
}

class StaffApiService {
  private rosterCache: CacheEntry<StaffRosterResponse> | null = null;
  private rosterInflight: Promise<StaffRosterResponse> | null = null;
  private profileCache = new Map<string, CacheEntry<StaffProfileResponse>>();
  private profileInflight = new Map<string, Promise<StaffProfileResponse>>();
  private ticketCountsCache: CacheEntry<StaffTicketCountsResponse> | null = null;
  private ticketCountsInflight: Promise<StaffTicketCountsResponse> | null = null;

  private async request<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${STAFF_API_BASE_URL}${endpoint}`, {
      method: 'GET',
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null) as { error?: string } | null;
      throw new StaffApiError(response.status, describeStatus(response.status, body?.error));
    }

    return response.json();
  }

  async getRoster(options: { force?: boolean } = {}): Promise<StaffRosterResponse> {
    const now = Date.now();
    if (!options.force && this.rosterCache && now - this.rosterCache.fetchedAt < ROSTER_TTL_MS) {
      return this.rosterCache.data;
    }
    if (this.rosterInflight) return this.rosterInflight;

    this.rosterInflight = this.request<StaffRosterResponse>('/profiles')
      .then((data) => {
        this.rosterCache = { data, fetchedAt: Date.now() };
        return data;
      })
      .finally(() => {
        this.rosterInflight = null;
      });
    return this.rosterInflight;
  }

  /** Synchronous read of the last roster fetch (for instant rail rendering). */
  getCachedRoster(): StaffRosterResponse | null {
    return this.rosterCache?.data ?? null;
  }

  async getProfile(discordId: string, options: { force?: boolean } = {}): Promise<StaffProfileResponse> {
    const now = Date.now();
    const cached = this.profileCache.get(discordId);
    if (!options.force && cached && now - cached.fetchedAt < PROFILE_TTL_MS) {
      return cached.data;
    }
    const inflight = this.profileInflight.get(discordId);
    if (inflight) return inflight;

    const promise = this.request<StaffProfileResponse>(`/profiles/${encodeURIComponent(discordId)}`)
      .then((data) => {
        this.profileCache.set(discordId, { data, fetchedAt: Date.now() });
        return data;
      })
      .finally(() => {
        this.profileInflight.delete(discordId);
      });
    this.profileInflight.set(discordId, promise);
    return promise;
  }

  /**
   * Tickets closed per staff member for the current month and the five before it, with each
   * finalized month's MVP and raffle standings. Shared by the roster, the profile page and the
   * leaderboard, so it is cached and de-duplicated like the roster.
   */
  async getTicketCounts(options: { force?: boolean } = {}): Promise<StaffTicketCountsResponse> {
    const now = Date.now();
    if (!options.force && this.ticketCountsCache && now - this.ticketCountsCache.fetchedAt < TICKET_COUNTS_TTL_MS) {
      return this.ticketCountsCache.data;
    }
    if (this.ticketCountsInflight) return this.ticketCountsInflight;

    this.ticketCountsInflight = this.request<StaffTicketCountsResponse>('/ticket-counts')
      .then((data) => {
        this.ticketCountsCache = { data, fetchedAt: Date.now() };
        return data;
      })
      .finally(() => {
        this.ticketCountsInflight = null;
      });
    return this.ticketCountsInflight;
  }

  /** Synchronous read of the last ticket-counts fetch (for instant first paint). */
  getCachedTicketCounts(): StaffTicketCountsResponse | null {
    return this.ticketCountsCache?.data ?? null;
  }

  clearCache(): void {
    this.rosterCache = null;
    this.profileCache.clear();
    this.ticketCountsCache = null;
  }
}

function describeStatus(status: number, serverMessage?: string): string {
  switch (status) {
    case 400:
      return 'That is not a valid Discord ID.';
    case 403:
      return serverMessage || 'You do not have permission to view staff profiles (argus.staff.read).';
    case 404:
      return serverMessage || 'No staff profile exists for this member.';
    case 429:
      return serverMessage || 'The staff profile service is rate limiting requests. Try again shortly.';
    case 502:
      return serverMessage || 'The staff profile service could not be reached.';
    case 503:
      return serverMessage || 'The staff profile proxy is not configured on the server yet.';
    default:
      return serverMessage || `API Error: ${status}`;
  }
}

export const staffApiService = new StaffApiService();
